import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Loader2, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    parent_name: '',
    phone: '',
    email: '',
    child_age: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.parent_name.trim()) newErrors.parent_name = 'Parent name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!/^\d{10}$/.test(formData.phone.replace(/[\s-]/g, ''))) newErrors.phone = 'Please enter a valid 10-digit phone number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.child_age) newErrors.child_age = 'Child age is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        setSubmitMessage(response.data.message);
        setFormData({
          parent_name: '',
          phone: '',
          email: '',
          child_age: '',
          city: ''
        });
        if (onSuccess) onSuccess();
      } else {
        setSubmitError(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.response?.data?.detail) {
        setSubmitError(error.response.data.detail);
      } else if (error.response?.data?.[0]?.msg) {
        setSubmitError(error.response.data[0].msg);
      } else {
        setSubmitError('Failed to submit form. Please try again or use the Google Form below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleReset = () => {
    setSubmitted(false);
    setSubmitMessage('');
    setSubmitError('');
  };

  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto bg-gradient-to-br from-emerald-50 to-blue-50 border-none shadow-lg" data-testid="success-card">
        <CardContent className="pt-12 pb-12 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Welcome to the Founding Families!</h3>
          <p className="text-gray-600 mb-6">{submitMessage || "Thank you for joining our exclusive waitlist. We'll be in touch soon with priority access to admissions and exclusive updates."}</p>
          <Button 
            onClick={handleReset}
            data-testid="submit-another-btn"
            className="bg-blue-900 hover:bg-blue-800 text-white"
          >
            Submit Another Entry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm border-gray-200 shadow-xl" data-testid="waitlist-form-card">
      <CardContent className="pt-8 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3" data-testid="form-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{submitError}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="parent_name" className="text-gray-700 font-medium">Parent/Guardian Name *</Label>
            <Input
              id="parent_name"
              data-testid="parent-name-input"
              type="text"
              value={formData.parent_name}
              onChange={(e) => handleChange('parent_name', e.target.value)}
              placeholder="Enter your full name"
              className="border-gray-300 focus:border-blue-900 focus:ring-blue-900"
            />
            {errors.parent_name && <p className="text-red-500 text-sm" data-testid="parent-name-error">{errors.parent_name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number (WhatsApp) *</Label>
            <Input
              id="phone"
              data-testid="phone-input"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="Enter your 10-digit phone number"
              className="border-gray-300 focus:border-blue-900 focus:ring-blue-900"
            />
            {errors.phone && <p className="text-red-500 text-sm" data-testid="phone-error">{errors.phone}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="border-gray-300 focus:border-blue-900 focus:ring-blue-900"
            />
            {errors.email && <p className="text-red-500 text-sm" data-testid="email-error">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="child_age" className="text-gray-700 font-medium">Child's Age *</Label>
            <Select value={formData.child_age} onValueChange={(value) => handleChange('child_age', value)}>
              <SelectTrigger className="border-gray-300 focus:border-blue-900 focus:ring-blue-900" data-testid="child-age-select">
                <SelectValue placeholder="Select age group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5-7" data-testid="age-5-7">5-7 years</SelectItem>
                <SelectItem value="8-10" data-testid="age-8-10">8-10 years</SelectItem>
                <SelectItem value="11-13" data-testid="age-11-13">11-13 years</SelectItem>
                <SelectItem value="14-16" data-testid="age-14-16">14-16 years</SelectItem>
                <SelectItem value="17+" data-testid="age-17">17+ years</SelectItem>
              </SelectContent>
            </Select>
            {errors.child_age && <p className="text-red-500 text-sm" data-testid="child-age-error">{errors.child_age}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="city" className="text-gray-700 font-medium">City *</Label>
            <Input
              id="city"
              data-testid="city-input"
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="e.g., Vadodara, Ahmedabad, Surat"
              className="border-gray-300 focus:border-blue-900 focus:ring-blue-900"
            />
            {errors.city && <p className="text-red-500 text-sm" data-testid="city-error">{errors.city}</p>}
          </div>

          <Button 
            type="submit" 
            data-testid="submit-waitlist-btn"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-6 text-lg shadow-lg transition-all duration-300 hover:shadow-xl"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Reserve Your Spot'
            )}
          </Button>

          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSdLB574ZzORg5G5i-W4C7Te9fcnATFUVDxJ8qC7NZmV4Opv3Q/viewform"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-form-link"
            className="block w-full"
          >
            <Button 
              type="button"
              variant="outline"
              className="w-full border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-semibold py-6 text-base transition-all duration-300"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Continue with Google Form
            </Button>
          </a>
        </form>
      </CardContent>
    </Card>
  );
};

export default WaitlistForm;
